import { MEyebrow } from "@cntrl/ui";
import { useMerchRollup } from "../../../app/hooks/useMerchRollup.js";
import { CardError, CardLoading } from "./mobileCard.js";
import { num, usd } from "./homeFormat.js";
import styles from "./MobileHome.module.scss";

/** The store-connection pill (ART-155 / Ch.08) — a live dot + the POS name (green) once wired, else a grey
 *  "NOT CONNECTED". Whether a store is ATTACHED is a different fact from today's sales, so the pill carries
 *  the connect-state and the boxes carry the money. */
function MerchConnPill({ connected, provider }: { connected: boolean; provider: string | null | undefined }) {
  const name = connected ? (provider ? provider[0].toUpperCase() + provider.slice(1) : "Connected") : "Not connected";
  return (
    <span className={styles.connPill} data-connected={connected || undefined}>
      {connected && <span className={styles.connPillDot} aria-hidden="true" />}
      {name}
    </span>
  );
}

function Box({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.box}>
      <span className={styles.boxLabel}>{label}</span>
      <span className={styles.boxValue}>{value}</span>
    </div>
  );
}

/**
 * MERCH (ART-182) — the consolidated merch section that replaced the split `merch_cut` + `pos_sales` cards
 * (and the retired fan-signup QR, ART-180). Four boxes: web pending / web sales 30d / POS today / POS 30d.
 *
 * <b>`posOnly` is the merch-account variant.</b> A merch seat's home lists `merch_pos` (NavigationDocument),
 * which renders only the two POS boxes — the in-person lane is that seat's whole job, and web sales are not
 * theirs to see. The decision is server-side (the widget key), so this component only honours the flag it is
 * handed rather than re-deriving the seat's role.
 *
 * KEEP-SHAPE when nothing is connected (the desktop `MerchRollupCard` rule): the figures degrade to em-dashes
 * (`usd(null)` / `num(null)` ARE the em-dash) and the header pill carries the connect-state, rather than the
 * body swapping for an empty state. A real `0` still reads `$0`.
 */
export function MerchSection({ posOnly }: { posOnly?: boolean }) {
  const q = useMerchRollup();
  const card = q.data;
  const connected = card?.connected === true;
  const r = card?.rollup ?? null;

  return (
    <section className={styles.merch} aria-label="Merch">
      <header className={styles.cardHead}>
        <MEyebrow>Merch</MEyebrow>
        {card && (
          <div className={styles.cardAction}>
            <MerchConnPill connected={connected} provider={card.provider} />
          </div>
        )}
      </header>
      {q.isLoading ? (
        <CardLoading />
      ) : q.isError ? (
        <CardError error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        <div className={styles.boxGrid}>
          {!posOnly && (
            <>
              <Box label="Pending web orders" value={num(connected ? r?.pendingCount : null)} />
              <Box label="Web sales · 30d" value={usd(connected ? r?.sales30dCents : null)} />
            </>
          )}
          <Box label="POS sales · Today" value={usd(connected ? r?.posSalesTodayCents : null)} />
          <Box label="POS sales · 30d" value={usd(connected ? r?.posSales30dCents : null)} />
        </div>
      )}
    </section>
  );
}

/** The full 4-box Merch section (owner / TM) — nav key `merch`. */
export function MerchSectionCard() {
  return <MerchSection />;
}

/** The POS-only Merch section (merch account) — nav key `merch_pos` (ART-182). */
export function MerchPosSectionCard() {
  return <MerchSection posOnly />;
}
